import axios, { AxiosError } from 'axios';
import { logout } from './ApiService';
import { ApiError } from '../models/ApiError';
import { defaultErrorHandler } from './ErrorHandler';
import { addBreadcrumb } from './sentry';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://127.0.0.1:3000/';

let isRefreshing = false;
let failedQueue: {
  resolve: (token: string) => void;
  reject: (error: any) => void;
}[] = [];

const processQueue = (error: any, token: string | null) => {
  failedQueue.forEach((prom) => {
    if (token) {
      prom.resolve(token);
    } else {
      prom.reject(error);
    }
  });
  failedQueue = [];
};

/**
 * Convert Axios error to ApiError using the ErrorHandler
 */
const convertAxiosErrorToApiError = (error: AxiosError): ApiError => {
  return defaultErrorHandler.handle(error);
};

const axiosInstance = axios.create({
  baseURL: API_BASE_URL,
});

/**
 * Attach access token (if exists) to request headers
 */
axiosInstance.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('idToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    // Add Sentry breadcrumb for request
    addBreadcrumb(
      `API Request: ${config.method?.toUpperCase()} ${config.url}`,
      'http',
      'info',
      {
        method: config.method,
        url: config.url,
        baseURL: config.baseURL,
      }
    );
    
    return config;
  },
  (error) => {
    // Add breadcrumb for request error
    addBreadcrumb(
      `API Request Error: ${error.message}`,
      'http',
      'error',
      { error: error.message }
    );
    return Promise.reject(error);
  }
);

/**
 * Handle 401: attempt to refresh the token, or if refresh fails => log out
 * Also converts all errors to ApiError format
 */
axiosInstance.interceptors.response.use(
  (response) => {
    // Add breadcrumb for successful response
    addBreadcrumb(
      `API Response: ${response.config.method?.toUpperCase()} ${response.config.url} - ${response.status}`,
      'http',
      'info',
      {
        method: response.config.method,
        url: response.config.url,
        status: response.status,
      }
    );
    return response;
  },
  async (error: AxiosError) => {
    const originalRequest = error.config as any;
    const requestUrl = originalRequest?.url || '';

    // Don't try to refresh token for login/auth endpoints
    const isAuthEndpoint = requestUrl.includes('/auth/login') || 
                          requestUrl.includes('/auth/refresh') ||
                          requestUrl.includes('/auth/forgot-password');

    // If unauthorized and we haven't retried yet, and it's not an auth endpoint
    if (error.response?.status === 401 && !originalRequest._retry && !isAuthEndpoint) {
      originalRequest._retry = true;

      // Check if we have tokens to refresh
      const refreshToken = localStorage.getItem('refreshToken');
      const userName = localStorage.getItem('userName');
      
      // If no refresh token, don't try to refresh - just convert and reject the error
      if (!refreshToken || !userName) {
        const apiError = convertAxiosErrorToApiError(error);
        return Promise.reject(apiError);
      }

      // If there's already a refresh in progress, add this request to the queue
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((newToken) => {
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
            return axiosInstance(originalRequest);
          })
          .catch((err) => {
            // Convert error to ApiError before rejecting
            const apiError = axios.isAxiosError(err) 
              ? convertAxiosErrorToApiError(err)
              : ApiError.fromUnknownError(err);
            return Promise.reject(apiError);
          });
      }

      // Otherwise, start refreshing
      isRefreshing = true;
      try {
        // Attempt to refresh
        const { data } = await axios.post(`${API_BASE_URL}/auth/refresh`, {
          refreshToken,
          userName
        });

        const newidToken = data.tokens.idToken;
        // const newRefreshToken = data.refreshToken;

        // Save new tokens
        localStorage.setItem('idToken', newidToken);
        // localStorage.setItem('refreshToken', newRefreshToken);

        // Process queue
        processQueue(null, newidToken);

        // Retry original
        originalRequest.headers.Authorization = `Bearer ${newidToken}`;
        return axiosInstance(originalRequest);

      } catch (refreshError) {
        // Convert refresh error to ApiError
        const apiRefreshError = axios.isAxiosError(refreshError)
          ? convertAxiosErrorToApiError(refreshError)
          : ApiError.fromUnknownError(refreshError);

        // If refresh fails, reject all queued requests
        processQueue(apiRefreshError, null);

        // Logout user (clears tokens, redirects to login)
        // The logout function will handle the redirect
        logout();

        return Promise.reject(apiRefreshError);

      } finally {
        isRefreshing = false;
      }
    }

    // Convert all errors to ApiError format before rejecting
    const apiError = convertAxiosErrorToApiError(error);
    return Promise.reject(apiError);
  }
);

export default axiosInstance;